import styled, {keyframes} from "styled-components";
import {hexToRGBA} from "@dropDesk/utils/helpers/colors";

type ContainerType = {
    background: string;
    border: string;
}

type ContainerButtonHeaderType = {
    background: string;
    accent: string;
    border: string;
};

type TextType = {
    color: string;
};

type SkeletonBoneType = {
    background: string;
    highlight: string;
    width?: string;
    height?: string;
    borderRadius?: string;
};

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const Container = styled.div<ContainerType>`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.background};
  height: calc(50vh - 103px);
  min-height: calc(50vh - 103px);
  width: 50%;
  max-width: 50%;
  overflow: hidden;

  border: 1px solid ${props => props.border};
  border-radius: 10px;
  margin-bottom: 16px;

  &:not(:last-of-type) {
    margin-right: 16px;
  }
`;

export const ChartArea = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
`;

export const ContainerButton = styled.div<ContainerButtonHeaderType>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  max-width: 24px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    background: ${props => props.background};
    border-color: transparent;
    box-shadow: 0 0 0 2px ${props => hexToRGBA(props.accent, .58)};
    outline: none;
    transition: box-shadow .3s ease-out .1s, border .3s ease-out, border-radius .3s ease-out, background-color .2s ease-out, color .2s ease-out, transform .2s ease-out;
  }
`;

export const HighlightRow = styled.div<{ border: string; background: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${(props) => props.border};
  background-color: ${(props) => props.background};
  flex-shrink: 0;
`;

export const HighlightText = styled.span<TextType>`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.color};
  line-height: 1.4;
  min-width: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SkeletonContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 16px 20px 20px;
  gap: 18px;
  min-height: 0;
  width: 100%;
`;

export const SkeletonTitle = styled.span<TextType>`
  align-self: center;
  color: ${(props) => props.color};
  font-family: Poppins;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
`;

export const SkeletonBars = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-evenly;
  gap: 14px;
  min-height: 0;
`;

export const SkeletonBarRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

export const SkeletonBone = styled.div<SkeletonBoneType>`
  width: ${(props) => props.width ?? '100%'};
  height: ${(props) => props.height ?? '14px'};
  border-radius: ${(props) => props.borderRadius ?? '6px'};
  background: linear-gradient(
    90deg,
    ${(props) => props.background} 25%,
    ${(props) => props.highlight} 50%,
    ${(props) => props.background} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  flex-shrink: 0;
`;
